// web/src/features/competition/wizard/WizardContext.tsx
import React, {
  createContext,
  Dispatch,
  ReactNode,
  useContext,
  useMemo,
  useReducer,
} from 'react'

export type WizardStep =
  | 'create'
  | 'clone'
  | 'info'
  | 'type'
  | 'structure'
  | 'structureDivisions'
  | 'structureConferences'
  | 'join'
  | 'summary'

export type CloseIntent = 'close' | 'skip' | null

export type WizardNavState = {
  currentStep: WizardStep
  history: WizardStep[]
  isSubmitting: boolean
  isConfirmCloseOpen: boolean
  closeIntent: CloseIntent
}

type Action =
  | { type: 'GO_TO'; step: WizardStep }
  | { type: 'GO_BACK' }
  | { type: 'REQUEST_CLOSE'; intent: Exclude<CloseIntent, null> }
  | { type: 'CANCEL_CLOSE' }
  | { type: 'SET_SUBMITTING'; value: boolean }
  /** Truncate forward history; keep only the prefix up to and including `keepUpTo`. */
  | { type: 'TRUNCATE_HISTORY'; keepUpTo: WizardStep }

const buildInitialState = (initialStep: WizardStep): WizardNavState => ({
  currentStep: initialStep,
  history: [],
  isSubmitting: false,
  isConfirmCloseOpen: false,
  closeIntent: null,
})

const reducer = (state: WizardNavState, action: Action): WizardNavState => {
  switch (action.type) {
    case 'GO_TO':
      if (action.step === state.currentStep) {
        return state
      }
      return {
        ...state,
        currentStep: action.step,
        history: [...state.history, state.currentStep],
      }
    case 'GO_BACK':
      if (state.history.length === 0) {
        return state
      }
      return {
        ...state,
        currentStep: state.history[state.history.length - 1],
        history: state.history.slice(0, -1),
      }
    case 'REQUEST_CLOSE':
      return { ...state, isConfirmCloseOpen: true, closeIntent: action.intent }
    case 'CANCEL_CLOSE':
      return { ...state, isConfirmCloseOpen: false, closeIntent: null }
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.value }
    case 'TRUNCATE_HISTORY': {
      const idx = state.history.lastIndexOf(action.keepUpTo)
      if (idx < 0) {
        return state
      }
      return { ...state, history: state.history.slice(0, idx + 1) }
    }
    default:
      return state
  }
}

type ContextValue = {
  state: WizardNavState
  dispatch: Dispatch<Action>
  goTo: (step: WizardStep) => void
  goBack: () => void
  goNext: (next: WizardStep) => void
  truncateHistory: (keepUpTo: WizardStep) => void
  requestClose: (intent: Exclude<CloseIntent, null>) => void
  cancelClose: () => void
  setSubmitting: (value: boolean) => void
}

const WizardContext = createContext<ContextValue | undefined>(undefined)

type Props = {
  children: ReactNode
  initialStep?: WizardStep
}

export const WizardProvider = ({ children, initialStep = 'create' }: Props) => {
  const [state, dispatch] = useReducer(reducer, initialStep, buildInitialState)

  const value = useMemo<ContextValue>(
    () => ({
      state,
      dispatch,
      goTo: (step) => dispatch({ type: 'GO_TO', step }),
      goBack: () => dispatch({ type: 'GO_BACK' }),
      goNext: (next) => dispatch({ type: 'GO_TO', step: next }),
      truncateHistory: (keepUpTo) =>
        dispatch({ type: 'TRUNCATE_HISTORY', keepUpTo }),
      requestClose: (intent) => dispatch({ type: 'REQUEST_CLOSE', intent }),
      cancelClose: () => dispatch({ type: 'CANCEL_CLOSE' }),
      setSubmitting: (val) => dispatch({ type: 'SET_SUBMITTING', value: val }),
    }),
    [state]
  )

  return (
    <WizardContext.Provider value={value}>{children}</WizardContext.Provider>
  )
}

export const useWizardNavigation = () => {
  const ctx = useContext(WizardContext)
  if (!ctx) {
    throw new Error('useWizardNavigation must be used inside <WizardProvider>')
  }
  return ctx
}
